import React, { forwardRef } from 'react';

import { Icon } from '../Icon/Icon';
import type { ButtonAsAnchorProps, ButtonAsButtonProps, ButtonProps } from './Button.types';
import * as styles from './Button.styles';

interface ButtonOverloads {
  (props: ButtonAsButtonProps & React.RefAttributes<HTMLButtonElement>): React.ReactElement | null;
  (props: ButtonAsAnchorProps & React.RefAttributes<HTMLAnchorElement>): React.ReactElement | null;
  displayName?: string;
}

function isAnchorProps(props: ButtonProps): props is ButtonAsAnchorProps {
  return (props as ButtonAsAnchorProps).href !== undefined;
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(
    {
      children,
      compact = false,
      fullWidth = false,
      iconLeft,
      iconRight,
      inverted = false,
      secondary,
      ...props
    },
    ref,
  ) {
    const { as, ...anchorProps } = props as ButtonAsAnchorProps;
    const hasBothIcons = Boolean(iconLeft && iconRight);

    if (hasBothIcons && process.env.NODE_ENV !== 'production') {
      throw new Error('[Button] `iconLeft` and `iconRight` are mutually exclusive.');
    }

    const iconSize = compact ? 'sm' : 'md';
    const buttonStyles = [
      styles.root,
      fullWidth ? styles.fullWidth : undefined,
      styles.getVariantStyles({ inverted, secondary }),
      compact ? styles.compact : undefined,
    ];
    const content = (
      <>
        {iconLeft ? (
          <span css={styles.icon}>
            <Icon aria-hidden name={iconLeft} size={iconSize} />
          </span>
        ) : null}
        <span css={styles.content}>{children}</span>
        {iconRight ? (
          <span css={styles.icon}>
            <Icon aria-hidden name={iconRight} size={iconSize} />
          </span>
        ) : null}
      </>
    );

    if (
      isAnchorProps({ children, compact, fullWidth, inverted, secondary, ...props } as ButtonProps)
    ) {
      if (as && as !== 'a') {
        return React.createElement(
          as as React.ElementType,
          {
            ...anchorProps,
            css: buttonStyles,
            ref: ref as React.Ref<unknown>,
          },
          content,
        );
      }

      return (
        <a
          {...(anchorProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          css={buttonStyles}
          ref={ref as React.Ref<HTMLAnchorElement>}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
        css={buttonStyles}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        {content}
      </button>
    );
  },
) as unknown as ButtonOverloads;

Button.displayName = 'Button';
